import { LegacyProductCode } from '@up2stay/up2promo-api-client';

export {
  isSetupProductType,
  parseSetupTypeParam,
  SETUP_PRODUCT_TYPES,
} from '@/core/lib/product-setup-type';

/**
 * Configure wizard phases (app adaptation of setup-frontend routes).
 * Single source of truth — do not scatter string literals.
 */
export const ConfigurePhase = {
  SelectType: 'select-type',
  Destination: 'destination',
  Location: 'location',
  CreateLocation: 'create-location',
  Username: 'username',
  CustomUrl: 'custom-url',
  Profile: 'profile',
  /** Optional product label — last configure step before create. */
  Description: 'description',
} as const;

export type ConfigurePhaseId =
  (typeof ConfigurePhase)[keyof typeof ConfigurePhase];

/**
 * Destination branch after type is known (setup-frontend input vs custom-url).
 */
export const DestinationChoice = {
  Location: 'location',
  Username: 'username',
  CustomUrl: 'customUrl',
} as const;

export type DestinationChoiceId =
  (typeof DestinationChoice)[keyof typeof DestinationChoice];

export function needsSelectTypePhase(
  productType: LegacyProductCode | undefined | null,
  opts?: { typeLocked?: boolean },
): boolean {
  if (opts?.typeLocked) return false;
  if (!productType) return true;
  // Factory CU without an explicit lock still needs a target-type pick.
  return productType === LegacyProductCode.CU;
}

export function usesUsernameFlow(
  productType: LegacyProductCode | undefined | null,
): boolean {
  return (
    productType === LegacyProductCode.IG || productType === LegacyProductCode.TT
  );
}

export function usesLocationPrimaryFlow(
  productType: LegacyProductCode | undefined | null,
): boolean {
  return (
    productType === LegacyProductCode.GO ||
    productType === LegacyProductCode.ME ||
    productType === LegacyProductCode.FB
  );
}

/**
 * Build ordered configure phases after scan.
 * Skips setup-frontend welcome + email (user is authenticated).
 *
 * `typeLocked` — true when type came from `?t=` / select-type pick / BC mode.
 * `typePicked` — the type was chosen on the select-type step; keep that step
 * in front so back returns to it (DEV-456).
 */
function withDescriptionPhase(phases: ConfigurePhaseId[]): ConfigurePhaseId[] {
  return [...phases, ConfigurePhase.Description];
}

export function buildConfigurePhases(opts: {
  productType: LegacyProductCode | undefined | null;
  destination: DestinationChoiceId | null;
  creatingLocation: boolean;
  typeLocked?: boolean;
  typePicked?: boolean;
  isProfileBcMode?: boolean;
}): ConfigurePhaseId[] {
  if (opts.isProfileBcMode) {
    return withDescriptionPhase([ConfigurePhase.Profile]);
  }

  const type = opts.productType ?? undefined;
  const typeLocked = opts.typeLocked === true;

  if (needsSelectTypePhase(type, { typeLocked })) {
    // Reserve longest remaining path so progress does not jump backward.
    const phases: ConfigurePhaseId[] = [
      ConfigurePhase.SelectType,
      ConfigurePhase.Destination,
      ConfigurePhase.Location,
    ];
    if (opts.creatingLocation) phases.push(ConfigurePhase.CreateLocation);
    return withDescriptionPhase(phases);
  }

  const phases = buildKnownTypePhases(type, opts);
  return opts.typePicked ? [ConfigurePhase.SelectType, ...phases] : phases;
}

function buildKnownTypePhases(
  type: LegacyProductCode | undefined,
  opts: { destination: DestinationChoiceId | null; creatingLocation: boolean },
): ConfigurePhaseId[] {
  const phases: ConfigurePhaseId[] = [];

  if (type === LegacyProductCode.BC) {
    return withDescriptionPhase([ConfigurePhase.Profile]);
  }

  if (type === LegacyProductCode.CU) {
    return withDescriptionPhase([ConfigurePhase.CustomUrl]);
  }

  phases.push(ConfigurePhase.Destination);

  const dest = opts.destination;
  if (dest === DestinationChoice.CustomUrl) {
    phases.push(ConfigurePhase.CustomUrl);
  } else if (dest === DestinationChoice.Username) {
    phases.push(ConfigurePhase.Username);
  } else {
    phases.push(ConfigurePhase.Location);
    if (opts.creatingLocation) phases.push(ConfigurePhase.CreateLocation);
  }

  return withDescriptionPhase(phases);
}

export function firstConfigurePhase(
  productType: LegacyProductCode | undefined | null,
  opts?: { typeLocked?: boolean; isProfileBcMode?: boolean },
): ConfigurePhaseId {
  const phases = buildConfigurePhases({
    productType,
    destination: null,
    creatingLocation: false,
    typeLocked: opts?.typeLocked,
    isProfileBcMode: opts?.isProfileBcMode,
  });
  return phases[0] ?? ConfigurePhase.SelectType;
}

export function destinationChoicesForType(
  productType: LegacyProductCode | undefined | null,
): DestinationChoiceId[] {
  if (
    productType === LegacyProductCode.IG ||
    productType === LegacyProductCode.TT
  ) {
    return [
      DestinationChoice.Username,
      DestinationChoice.Location,
      DestinationChoice.CustomUrl,
    ];
  }
  if (
    productType === LegacyProductCode.GO ||
    productType === LegacyProductCode.ME ||
    productType === LegacyProductCode.FB
  ) {
    return [DestinationChoice.Location, DestinationChoice.CustomUrl];
  }
  if (productType === LegacyProductCode.CU) {
    return [DestinationChoice.CustomUrl];
  }
  if (productType === LegacyProductCode.BC) {
    return [];
  }
  return [DestinationChoice.Location, DestinationChoice.CustomUrl];
}

export function selectableSetupTypes(
  includeBusinessCard: boolean,
): LegacyProductCode[] {
  const base: LegacyProductCode[] = [
    LegacyProductCode.GO,
    LegacyProductCode.IG,
    LegacyProductCode.TT,
    LegacyProductCode.FB,
    LegacyProductCode.ME,
    LegacyProductCode.CU,
  ];
  return includeBusinessCard ? [...base, LegacyProductCode.BC] : base;
}
